import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Clock, ChevronLeft, ChevronRight, Trophy, AlertCircle } from 'lucide-react';
import { Quiz, QuizQuestion, QuizAttempt } from '@/types/quiz';

interface QuizInterfaceProps {
  isOpen: boolean;
  onClose: () => void;
  quiz: Quiz;
  attempt: QuizAttempt | null;
  questions: QuizQuestion[];
  answers: Record<string, string>;
  onSaveAnswer: (questionId: string, answer: string, optionId?: string) => void;
  onSubmitQuiz: () => void;
  loading: boolean;
}

export const QuizInterface = ({
  isOpen,
  onClose,
  quiz,
  attempt,
  questions,
  answers,
  onSaveAnswer,
  onSubmitQuiz,
  loading
}: QuizInterfaceProps) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const currentQuestion = questions[currentQuestionIndex] || null;
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;
  const answeredCount = Object.keys(answers).length;

  // Timer logic
  const handleSubmit = useCallback(() => {
    if (answeredCount < questions.length) {
      setShowSubmitConfirm(true);
    } else {
      onSubmitQuiz();
    }
  }, [answeredCount, questions.length, onSubmitQuiz]);

  useEffect(() => {
    if (!quiz.time_limit || !attempt || attempt.is_completed) return;

    const startTime = new Date(attempt.created_at).getTime();
    const timeLimit = quiz.time_limit * 60 * 1000; // Convert to milliseconds

    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, timeLimit - elapsed);
      
      setTimeRemaining(remaining);
      
      if (remaining === 0) {
        handleSubmit();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [quiz.time_limit, attempt, handleSubmit]);

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (value: string, optionId?: string) => {
    onSaveAnswer(currentQuestion.id, value, optionId);
  };


  const renderQuestion = () => {
    if (!currentQuestion) return null;

    const currentAnswer = answers[currentQuestion.id] || '';

    switch (currentQuestion.question_type) {
      case 'multiple_choice':
        return (
          <RadioGroup 
            value={currentAnswer} 
            onValueChange={(value) => {
              const option = currentQuestion.options?.find(opt => opt.id === value);
              handleAnswerChange(option?.option_text || '', value);
            }}
            className="space-y-3"
          >
            {currentQuestion.options?.map((option) => (
              <div key={option.id} className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-gray-50">
                <RadioGroupItem value={option.id} id={option.id} />
                <Label htmlFor={option.id} className="flex-1 cursor-pointer">
                  {option.option_text}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'true_false':
        return (
          <RadioGroup 
            value={currentAnswer} 
            onValueChange={(value) => handleAnswerChange(value)}
            className="space-y-3"
          >
            <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-gray-50">
              <RadioGroupItem value="true" id="true" />
              <Label htmlFor="true" className="flex-1 cursor-pointer">True</Label>
            </div>
            <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-gray-50">
              <RadioGroupItem value="false" id="false" />
              <Label htmlFor="false" className="flex-1 cursor-pointer">False</Label>
            </div>
          </RadioGroup>
        );

      case 'fill_in_blank':
        return (
          <div className="space-y-4">
            <Input
              value={currentAnswer}
              onChange={(e) => handleAnswerChange(e.target.value)}
              placeholder="Type your answer here..."
              className="text-lg p-4"
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (!attempt || !currentQuestion) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center justify-between">
            <span>{quiz.title}</span>
            {timeRemaining !== null && (
              <Badge variant="outline" className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatTime(timeRemaining)}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Progress */}
          <div className="flex-shrink-0 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">
                Question {currentQuestionIndex + 1} of {questions.length}
              </span>
              <span className="text-sm text-gray-600">
                {answeredCount} answered
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Question */}
          <div className="flex-1 overflow-auto">
            <Card>
              <CardContent className="p-6">
                <div className="mb-6">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-semibold flex-1">
                      {currentQuestion.question_text}
                    </h3>
                    <Badge variant="outline">
                      {currentQuestion.points_value} points
                    </Badge>
                  </div>
                  
                  {currentQuestion.explanation && (
                    <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg mb-4">
                      <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-800">{currentQuestion.explanation}</p>
                    </div>
                  )}
                </div>

                {renderQuestion()}
              </CardContent>
            </Card>
          </div>

          {/* Navigation */}
          <div className="flex-shrink-0 flex justify-between items-center mt-6 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
              disabled={currentQuestionIndex === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>

            <div className="flex gap-2">
              {currentQuestionIndex === questions.length - 1 ? (
                <Button 
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Trophy className="h-4 w-4 mr-2" />
                  Submit Quiz
                </Button>
              ) : (
                <Button
                  onClick={() => setCurrentQuestionIndex(Math.min(questions.length - 1, currentQuestionIndex + 1))}
                  disabled={currentQuestionIndex === questions.length - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Submit confirmation dialog */}
        {showSubmitConfirm && (
          <Dialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Submit Quiz?</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <p className="text-gray-600 mb-4">
                  You have answered {answeredCount} out of {questions.length} questions. 
                  Unanswered questions will be marked as incorrect.
                </p>
                <p className="font-medium">Are you sure you want to submit?</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowSubmitConfirm(false)}>
                  Continue Quiz
                </Button>
                <Button 
                  onClick={() => {
                    setShowSubmitConfirm(false);
                    onSubmitQuiz();
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Submit
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};
